import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE = 15;

const DRIFT_FIELDS = [
  { id: "subweight", label: "Subwt" },
  { id: "ptAssay", label: "Pt" },
  { id: "pdAssay", label: "Pd" },
  { id: "rhAssay", label: "Rh" },
];

function driftTone(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "neutral";
  const n = Number(pct);
  if (n <= 5) return "low";
  if (n <= 20) return "moderate";
  if (n <= 50) return "high";
  return "very-high";
}

function formatDrift(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value)}%`;
}

export function AssayDriftPrioritySection({ summary }) {
  const items = summary?.prioritizedOems || [];
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset(0);
  }, [summary]);

  if (!items.length) {
    return null;
  }

  const pageItems = items.slice(offset, offset + PAGE_SIZE);
  const areMoreItemsAvailable = offset + PAGE_SIZE < items.length;
  const from = offset + 1;
  const to = offset + pageItems.length;

  return (
    <div className="converter-testing-priority-section">
      <div className="converter-testing-priority-header">
        <div>
          <div className="converter-testing-executive-title">Combined drift priority</div>
          <p className="converter-testing-drift-help c-gray fs-13 mb-0">
            A converter can drift on multiple fields at once. Ranked by combined drift
            (Subweight + Pt + Pd + Rh drift %). Highest priority first.
          </p>
        </div>
        <div className="converter-testing-priority-badge">{items.length.toLocaleString()}</div>
      </div>

      <div className="converter-testing-details-meta">
        Showing {from}–{to} of {items.length.toLocaleString()} tested converters
      </div>

      <div className="converter-testing-drift-wrap">
        <table className="converter-testing-drift-table converter-testing-priority-table">
          <thead>
            <tr>
              <th className="wt-40">#</th>
              <th>OEM</th>
              <th>Type</th>
              {DRIFT_FIELDS.map((field) => (
                <th key={field.id} className="t-center">{field.label}</th>
              ))}
              <th className="t-center">Combined</th>
              <th className="t-center">Tests</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item, index) => (
              <tr key={item.productId}>
                <td className="fw-bolder c-gray">{offset + index + 1}</td>
                <td>
                  <Link className="converter-testing-link" to={`/converter/${item.productId}`}>
                    {item.oem || item.productId}
                  </Link>
                  {(item.highDriftFieldCount ?? 0) > 1 && (
                    <span
                      className="converter-testing-priority-multi-badge"
                      title={`High drift on ${item.highDriftFieldCount} fields`}
                    >
                      {item.highDriftFieldCount} fields
                    </span>
                  )}
                </td>
                <td>{item.type || "—"}</td>
                {DRIFT_FIELDS.map((field) => {
                  const pct = item.driftPct?.[field.id];
                  return (
                    <td key={field.id} className="t-center">
                      <span className={`converter-testing-priority-drift converter-testing-priority-drift-${driftTone(pct)}`}>
                        {formatDrift(pct)}
                      </span>
                    </td>
                  );
                })}
                <td className="t-center fw-bolder converter-testing-priority-score">
                  {item.priorityScore != null ? `${item.priorityScore}%` : "—"}
                </td>
                <td className="t-center">{item.testCount ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="converter-testing-pagination">
        <button
          type="button"
          className="converter-testing-page-btn converter-testing-page-btn-prev"
          disabled={offset === 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
        >
          Previous
        </button>
        <button
          type="button"
          className="converter-testing-page-btn converter-testing-page-btn-next"
          disabled={!areMoreItemsAvailable}
          onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
